import { useEffect, useMemo, useState } from 'react';
import { Link } from 'wouter';
import { useDevices } from '../context/DevicesContext.js';
import { useAuth } from '../context/AuthContext.js';
import { homes as homesApi, type Home } from '../lib/api.js';
import { EntityCard } from '../components/entity-cards/EntityCard.js';
import { BridgeIndicator } from '../components/BridgeIndicator.js';
import { useT } from '../i18n/index.js';

export default function DashboardPage() {
  const { entities, bridges, wsStatus } = useDevices();
  const { accessToken } = useAuth();
  const t = useT();
  const [homes, setHomes] = useState<Home[] | null>(null);

  useEffect(() => {
    if (!accessToken) return;
    homesApi
      .list()
      .then((r) => setHomes(r.homes))
      .catch(() => setHomes([]));
  }, [accessToken]);

  const grouped = useMemo(() => {
    const map = new Map<string, Array<[string, (typeof entities)[string]]>>();
    for (const [id, entity] of Object.entries(entities)) {
      const area =
        typeof entity.attributes['area_id'] === 'string'
          ? (entity.attributes['area_id'] as string)
          : t('dashboard.unsorted');
      const list = map.get(area) ?? [];
      list.push([id, entity]);
      map.set(area, list);
    }
    return Array.from(map.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [entities, t]);

  // First-run: show the unified welcome whenever the user has no live
  // connection — no home at all, OR has a home but no bridge paired yet.
  // Direct HA users have already been redirected above.
  const needsOnboarding =
    homes !== null && (homes.length === 0 || (homes.length > 0 && bridges.length === 0));

  if (needsOnboarding) {
    return <WelcomeScreen />;
  }

  const isLoading = wsStatus === 'connecting' && Object.keys(entities).length === 0;

  return (
    <div className="max-w-5xl mx-auto p-4 flex flex-col gap-6">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold" style={{ color: 'var(--ac-text)' }}>
          {t('nav.home')}
        </h1>
        <BridgeIndicator bridges={bridges} wsStatus={wsStatus} />
      </div>

      {isLoading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <div
              key={i}
              className="h-24 rounded-xl border animate-pulse"
              style={{ backgroundColor: 'var(--ac-surface)', borderColor: 'var(--ac-border)' }}
            />
          ))}
        </div>
      ) : grouped.length === 0 ? (
        <div className="text-center py-12" style={{ color: 'var(--ac-text-muted)' }}>
          {t('dashboard.no_devices')}
        </div>
      ) : (
        grouped.map(([area, list]) => (
          <section key={area} className="flex flex-col gap-2">
            <h2
              className="text-sm uppercase tracking-wide"
              style={{ color: 'var(--ac-text-muted)' }}
            >
              {area}
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {list.map(([id, entity]) => (
                <EntityCard key={id} entityId={id} entity={entity} />
              ))}
            </div>
          </section>
        ))
      )}
    </div>
  );
}

function WelcomeScreen() {
  return (
    <div className="max-w-xl mx-auto p-6 pt-12 flex flex-col gap-6">
      <div className="text-center">
        <h1 className="text-3xl font-semibold" style={{ color: 'var(--ac-text)' }}>
          Welcome to AetherCasa
        </h1>
        <p className="mt-2 text-sm" style={{ color: 'var(--ac-text-muted)' }}>
          Connect your Home Assistant to see and control your devices.
        </p>
      </div>

      <Link
        href="/activate"
        className="group rounded-xl p-5 flex items-start gap-4 transition"
        style={{
          background: 'var(--ac-accent)',
          color: 'var(--ac-on-accent)',
        }}
      >
        <div
          className="shrink-0 w-10 h-10 rounded-lg flex items-center justify-center text-xl"
          style={{ background: 'rgba(255,255,255,0.18)' }}
          aria-hidden
        >
          ⚡
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-semibold">Connect your Home Assistant bridge</p>
          <p className="text-sm opacity-90 mt-1">
            Enter the activation code shown by the AetherCasa add-on in Home Assistant.
          </p>
        </div>
        <span className="shrink-0 text-xl opacity-80 group-hover:translate-x-0.5 transition">
          →
        </span>
      </Link>

      <Link
        href="/settings/homes"
        className="text-center text-xs underline"
        style={{ color: 'var(--ac-text-muted)' }}
      >
        Manage homes and bridges
      </Link>
    </div>
  );
}
